import { createContext, useState, useEffect, useContext, useCallback, useMemo } from "react";
import AuthService from "../services/AuthService";
import { adminApi } from "../services/ApiService";
import { ALL_SECTIONS, ROLE_DEFAULTS, mergeRolePermissions } from "../constants/permissions";
import { ROLES } from "../constants/config";

const AuthContext = createContext();

/**
 * Hook para acceder al contexto de autenticación
 * @throws {Error} Si se usa fuera del AuthProvider
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

/**
 * Provider de autenticación
 * Principios SOLID aplicados:
 * - SRP: Solo gestiona el estado de autenticación en React
 * - DIP: Delega la lógica de autenticación al AuthService
 */
export const AuthProvider = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isReseller, setIsReseller] = useState(false);
    const [isAlsoReseller, setIsAlsoReseller] = useState(false);
    const [adminRole, setAdminRole] = useState(null);
    const [adminRoles, setAdminRoles] = useState([]);
    const [needsNewPassword, setNeedsNewPassword] = useState(false);
    const [adminPermissions, setAdminPermissions] = useState(ALL_SECTIONS);
    const [error, setError] = useState(null);

    useEffect(() => {
        checkUserSession();
    }, []);

    /**
     * Verifica si hay una sesión activa al cargar
     */
    const checkUserSession = async () => {
        try {
            const { isValid, user: sessionUser } = await AuthService.checkAdminSession();
            
            if (isValid && sessionUser) {
                setUser(sessionUser);
                setIsAdmin(true);
                const resellerOnly = AuthService.isReseller(sessionUser);
                const groups = sessionUser.groups || [];
                const alsoReseller = !resellerOnly && groups.includes('reseller');
                setIsReseller(resellerOnly);
                setIsAlsoReseller(alsoReseller);
                if (resellerOnly) {
                    setAdminRole('reseller');
                    setAdminRoles(['reseller']);
                    setAdminPermissions(ROLE_DEFAULTS.reseller || []);
                } else {
                    const role = groups.includes('superadmin') ? 'superadmin' : 'admin';
                    setAdminRole(role);
                    if (role === 'superadmin') {
                        setAdminRoles(alsoReseller ? ['superadmin', 'reseller'] : ['superadmin']);
                        setAdminPermissions(alsoReseller ? [...ALL_SECTIONS, 'reseller-portal'] : ALL_SECTIONS);
                    } else {
                        loadPermissionsFromApi(sessionUser.email, role);
                    }
                }
            } else {
                setError('No tienes permisos de administrador');
            }
        } catch (error) {
            console.log('No hay sesión activa:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Maneja el inicio de sesión
     */
    const handleLogin = async (email, password) => {
        setError(null);
        setLoading(true);

        try {
            const result = await AuthService.login(email, password);
            
            if (result.success && result.user) {
                setUser(result.user);
                setIsAdmin(true);
                const resellerOnly = AuthService.isReseller(result.user);
                const groups = result.user.groups || [];
                const alsoReseller = !resellerOnly && groups.includes('reseller');
                setIsReseller(resellerOnly);
                setIsAlsoReseller(alsoReseller);
                if (resellerOnly) {
                    setAdminRole('reseller');
                    setAdminRoles(['reseller']);
                    setAdminPermissions(ROLE_DEFAULTS.reseller || []);
                } else {
                    const role = groups.includes('superadmin') ? 'superadmin' : 'admin';
                    setAdminRole(role);
                    if (role === 'superadmin') {
                        setAdminRoles(alsoReseller ? ['superadmin', 'reseller'] : ['superadmin']);
                        setAdminPermissions(alsoReseller ? [...ALL_SECTIONS, 'reseller-portal'] : ALL_SECTIONS);
                    } else {
                        loadPermissionsFromApi(result.user.email, role);
                    }
                }
                return { success: true };
            } else if (result.needsNewPassword) {
                setNeedsNewPassword(true);
                return { success: false, needsNewPassword: true };
            } else {
                setError(result.error);
                return { success: false, error: result.error };
            }
        } catch (error) {
            const errorMsg = error.message || 'Error al iniciar sesión';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    };

    /**
     * Completa el cambio de contraseña forzado
     */
    const handleCompleteNewPassword = async (newPassword) => {
        setError(null);
        setLoading(true);
        try {
            const result = await AuthService.completeNewPassword(newPassword);
            if (result.success && result.user) {
                setNeedsNewPassword(false);
                setUser(result.user);
                setIsAdmin(true);
                const groups = result.user.groups || [];
                const role = groups.includes('superadmin') ? 'superadmin' : 'admin';
                setAdminRole(role);
                setAdminPermissions(ALL_SECTIONS);
                return { success: true };
            } else {
                setError(result.error);
                return { success: false, error: result.error };
            }
        } catch (error) {
            const errorMsg = error.message || 'Error al cambiar contraseña';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    };

    /**
     * Maneja el cierre de sesión
     */
    const handleLogout = async () => {
        try {
            await AuthService.logout();
            setUser(null);
            setIsAdmin(false);
            setIsReseller(false);
            setIsAlsoReseller(false);
            setAdminRole(null);
            setAdminRoles([]);
            setAdminPermissions(ALL_SECTIONS);
            setError(null);
        } catch (error) {
            console.error('Error en logout:', error);
            setUser(null);
            setIsAdmin(false);
            setIsReseller(false);
            setIsAlsoReseller(false);
            setAdminRole(null);
            setAdminRoles([]);
            setAdminPermissions(ALL_SECTIONS);
        }
    };

    const loadPermissionsFromApi = useCallback(async (email, fallbackRole) => {
        try {
            const res = await adminApi.get('/admin/admin-users');
            const me = (res.users || []).find(u => u.email === email);
            if (me) {
                const roles = me.roles || [me.role || fallbackRole];
                const primaryRole = roles.find(r => r !== 'reseller') || roles[0];
                if (primaryRole && primaryRole !== 'admin') setAdminRole(primaryRole);
                setAdminRoles(roles);
                if (roles.includes('reseller')) setIsAlsoReseller(true);
                if (me.permissions) {
                    const perms = typeof me.permissions === 'string' ? JSON.parse(me.permissions) : me.permissions;
                    if (Array.isArray(perms) && perms.length > 0) {
                        // Ensure reseller-portal is included if user has reseller role
                        const finalPerms = roles.includes('reseller') && !perms.includes('reseller-portal')
                            ? [...perms, 'reseller-portal'] : perms;
                        setAdminPermissions(finalPerms);
                        return;
                    }
                }
                // No custom permissions — merge defaults from all roles
                setAdminPermissions(mergeRolePermissions(roles));
                return;
            }
        } catch (e) { console.warn('Could not load permissions from API:', e); }
        setAdminPermissions(ROLE_DEFAULTS[fallbackRole] || ALL_SECTIONS);
    }, []);

    const hasPermission = useCallback((sectionKey) => {
        if (adminRole === 'superadmin') return true;
        return adminPermissions.includes(sectionKey);
    }, [adminRole, adminPermissions]);

    const contextValue = useMemo(() => ({
        user,
        isAdmin,
        isReseller,
        isAlsoReseller,
        adminRole,
        adminRoles,
        adminPermissions,
        hasPermission,
        loading,
        error,
        needsNewPassword,
        handleLogin,
        handleCompleteNewPassword,
        handleLogout
    }), [user, isAdmin, isReseller, isAlsoReseller, adminRole, adminRoles, adminPermissions, hasPermission, loading, error, needsNewPassword]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
